import React, { useEffect, useState } from "react";
import { get } from "../services";

const whyChooseUs = [
  {
    icon: "💎",
    title: "Trusted Vendors",
    desc: "All vendors are carefully vetted to ensure quality, reliability, and professionalism for your events.",
  },
  {
    icon: "⚡",
    title: "Seamless Booking",
    desc: "Book your favorite vendors in just a few clicks with our intuitive and user-friendly platform.",
  },
  {
    icon: "🤝",
    title: "Dedicated Support",
    desc: "Our support team is always available to help you with any questions or event needs.",
  },
  {
    icon: "🔒",
    title: "Secure Payments",
    desc: "Enjoy peace of mind with secure, transparent, and hassle-free payment options.",
  },
];

const searchFeatures = [
  {
    type: "Search",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop",
    features: [
      "Basic vendor search",
      "Category filtering",
      "Location-based results",
      "Simple interface",
    ],
    capacity: "❌",
    naturalLanguage: "❌",
    allBasicFeatures: "✅",
    separateCodebase: "✅",
  },
  {
    type: "Advanced Search",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
    features: [
      "Advanced filtering",
      "Price range selection",
      "Rating filters",
      "Availability checking",
    ],
    capacity: "✅",
    naturalLanguage: "❌",
    allBasicFeatures: "✅",
    separateCodebase: "✅",
  },
  {
    type: "AI Search",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop",
    features: [
      "Natural language queries",
      "Smart recommendations",
      "Contextual understanding",
      "Personalized results",
    ],
    capacity: "✅",
    naturalLanguage: "✅",
    allBasicFeatures: "✅",
    separateCodebase: "✅",
  },
];

const timeline = [
  {
    year: "2024",
    title: "The Birth of EventNova",
    description:
      "EventNova was conceived and birthed, setting the foundation for Nigeria's most sophisticated event services marketplace. Our vision to transform event planning began to take shape.",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=300&h=200&fit=crop",
  },
  {
    year: "2025",
    title: "Official Launch",
    description: "EventNova will officially launch, bringing innovative search technology, AI-powered recommendations, and seamless vendor connections to event planners across Nigeria.",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=300&h=200&fit=crop",
  },
];

const About: React.FC = () => {
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [testimonialsError, setTestimonialsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoadingTestimonials(true);
      setTestimonialsError(null);
      try {
        const data = await get("/api/reviews/testimonials?limit=");
        if (data && Array.isArray(data.data)) {
          setTestimonials(data.data);
        } else {
          setTestimonials([]);
        }
      } catch (error) {
        setTestimonialsError("Failed to load testimonials. Please try again later.");
        setTestimonials([]);
      } finally {
        setLoadingTestimonials(false);
      }
    };

    fetchTestimonials();
  }, []);

  return (
    <div
      style={{
        background: "linear-gradient(120deg, #f8f8ff 0%, #e0e7ff 100%)",
        minHeight: "100vh",
        paddingBottom: 60,
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .about-banner-section {
            padding: 40px 16px !important;
          }
          .about-banner-heading {
            font-size: 32px !important;
          }
          .about-banner-subtitle {
            font-size: 18px !important;
          }
          .about-banner-tagline {
            font-size: 14px !important;
            padding: 16px !important;
          }
          .about-hero-section {
            padding: 32px 16px !important;
          }
          .about-hero-text {
            font-size: 16px !important;
            padding: 0 16px !important;
          }
          .about-mission-vision-section {
            margin: 24px 16px !important;
          }
          .about-mission-card,
          .about-vision-card {
            padding: 24px 16px !important;
          }
          .about-mission-card h2,
          .about-vision-card h2 {
            font-size: 24px !important;
          }
          .about-mission-card p,
          .about-vision-card p {
            font-size: 16px !important;
          }
          .about-join-section {
            padding: 24px 16px !important;
            margin: 24px 16px !important;
            flex-direction: column !important;
          }
          .about-join-section h2 {
            font-size: 24px !important;
          }
          .about-join-section p {
            font-size: 16px !important;
          }
        }
      `}</style>
      {/* Banner Section */}
      <section
        className="about-banner-section"
        style={{
          background:
            "linear-gradient(rgba(108, 99, 255, 0.8), rgba(183, 184, 247, 0.8)), url('https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1200&h=400&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          padding: "80px 0",
          textAlign: "center",
          color: "#fff",
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
          <h1
            className="about-banner-heading"
            style={{
              fontSize: 56,
              fontWeight: 900,
              marginBottom: 20,
              color: "#ffffff",
              textShadow:
                "0 4px 12px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3), 0 0 0 rgba(0,0,0,0.2)",
              letterSpacing: "-0.5px",
            }}
          >
            About EventNova
          </h1>
          <p
            className="about-banner-subtitle"
            style={{
              fontSize: 24,
              fontWeight: 600,
              marginBottom: 24,
              color: "#ffffff",
              textShadow:
                "0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            Where Innovation Meets Experience
          </p>
          <div
            className="about-banner-tagline"
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: 12,
              padding: "20px",
              display: "inline-block",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <p
              style={{
                fontSize: 18,
                margin: 0,
                lineHeight: 1.6,
                color: "#ffffff",
                fontWeight: 500,
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              Nigeria's Most Sophisticated Event Services Marketplace
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section
        className="about-hero-section"
        style={{
          padding: "64px 0 32px 0",
          textAlign: "center",
          background: "#fff",
          boxShadow: "0 2px 16px #eee",
        }}
      >
        <p
          className="about-hero-text"
          style={{
            fontSize: 20,
            color: "#333",
            maxWidth: 800,
            margin: "0 auto 18px auto",
            lineHeight: 1.7,
            fontWeight: 400,
          }}
        >
          EventNova is Nigeria's most sophisticated event services marketplace a
          smart, seamless platform designed to connect you with top-tier vendors
          who turn ordinary gatherings into extraordinary experiences. Whether
          you're planning a wedding that feels like a royal affair, a product
          launch that leaves a lasting impression, or a private dinner with
          exquisite ambiance we bring the talent, tech, and tools to make it
          happen.
        </p>
        <p
          className="about-hero-text"
          style={{
            fontSize: 20,
            color: "#333",
            maxWidth: 800,
            margin: "0 auto 18px auto",
            lineHeight: 1.7,
            fontWeight: 400,
          }}
        >
          We're not just a directory. We're a digital concierge powered by
          cutting-edge AI, helping you discover the perfect decorators, DJs,
          caterers, emcees, photographers, makeup artists, light & sound
          engineers, drone cinematographers, rental suppliers, luxury transport
          providers, stage designers, and more all vetted, verified, and
          reviewed.
        </p>
        <p
          className="about-hero-text"
          style={{
            fontSize: 20,
            color: "#555",
            maxWidth: 800,
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          From Lagos to Abuja, Port Harcourt to Enugu if there's a celebration
          brewing, EventNova is the intelligent link between vision and reality.
        </p>
      </section>

      {/* Mission & Vision (Separate Cards) */}
      <section
        className="about-mission-vision-section"
        style={{
          maxWidth: 1100,
          margin: "48px auto 0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        {/* Mission Card */}
        <div
          className="about-mission-card"
          style={{
            background: "#fff",
            borderRadius: 18,
            boxShadow: "0 4px 24px #eee",
            padding: 40,
            borderLeft: "6px solid #6C63FF",
          }}
        >
          <h2
            style={{
              color: "#6C63FF",
              fontWeight: 800,
              fontSize: 36,
              marginBottom: 20,
            }}
          >
            Our Mission
          </h2>
          <p
            style={{
              color: "#333",
              fontSize: 18,
              lineHeight: 1.7,
              marginBottom: 16,
              fontWeight: 400,
            }}
          >
            To transform the way events are planned in Nigeria by building a
            refined digital ecosystem where clients can effortlessly discover
            exceptional vendors, and service providers can thrive, grow, and
            showcase their craft.
          </p>
          <p
            style={{
              color: "#333",
              fontSize: 18,
              lineHeight: 1.7,
              fontWeight: 400,
            }}
          >
            We exist to remove friction from the planning process, to empower
            creativity, and to make every event no matter the size a seamless
            celebration of excellence.
          </p>
        </div>

        {/* Vision Card */}
        <div
          className="about-vision-card"
          style={{
            background: "linear-gradient(135deg, #6C63FF 0%, #b7b8f7 100%)",
            borderRadius: 18,
            boxShadow: "0 4px 24px #eee",
            padding: 40,
            color: "#fff",
          }}
        >
          <h2 style={{ fontWeight: 800, fontSize: 36, marginBottom: 20 }}>
            Our Vision
          </h2>
          <p
            style={{
              fontSize: 18,
              lineHeight: 1.7,
              marginBottom: 16,
              color: "#ffffff",
              fontWeight: 400,
              textShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          >
            To become Nigeria's most trusted and intelligent event platform a
            place where creativity thrives, excellence is celebrated, and every
            event begins with confidence and ends in brilliance.
          </p>
          <p
            style={{
              fontSize: 18,
              lineHeight: 1.7,
              color: "#ffffff",
              fontWeight: 400,
              textShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          >
            We envision a future where hosts, planners, and top-tier vendors
            connect seamlessly through technology, transforming moments into
            memories and ideas into extraordinary experiences.
          </p>
        </div>
      </section>

      {/* Join Us Early Section */}
      <section
        className="about-join-section"
        style={{
          maxWidth: 1100,
          margin: "48px auto 0 auto",
          padding: 32,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          display: "flex",
          alignItems: "center",
          gap: 32,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            flex: "1 1 320px",
            minWidth: 280,
            background: "#e0e7ff",
            borderRadius: 12,
            boxShadow: "0 2px 8px #e0e7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 220,
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=500&h=350&fit=crop"
            alt="Join Us Early"
            style={{
              width: "100%",
              borderRadius: 12,
              objectFit: "cover",
              minHeight: 220,
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
        <div style={{ flex: "2 1 400px", minWidth: 280 }}>
          <h2
            style={{
              color: "#6C63FF",
              fontWeight: 800,
              fontSize: 32,
              marginBottom: 16,
            }}
          >
            Join Us Early
          </h2>
          <p
            style={{
              color: "#333",
              fontSize: 18,
              marginBottom: 18,
              fontWeight: 400,
            }}
          >
            EventNova is just getting started and you can be part of our
            founding community! We’re currently live in Abuja and looking for
            passionate users and vendors to help shape the future of event
            planning in Nigeria.
          </p>
          <ul
            style={{
              color: "#333",
              fontSize: 16,
              marginBottom: 18,
              paddingLeft: 20,
              fontWeight: 400,
            }}
          >
            <li>Get early access to new features</li>
            <li>Help us build a platform that truly serves your needs</li>
            <li>Enjoy exclusive perks as an early adopter</li>
            <li>Be part of a growing, innovative community</li>
          </ul>
          <a
            href="/vendors"
            style={{
              background: "#6C63FF",
              color: "#fff",
              fontWeight: 700,
              fontSize: 18,
              padding: "12px 32px",
              borderRadius: 8,
              textDecoration: "none",
              boxShadow: "0 2px 8px #eee",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = "#fff";
              (e.currentTarget as HTMLAnchorElement).style.color = "#6C63FF";
              (e.currentTarget as HTMLAnchorElement).style.border =
                "2px solid #6C63FF";
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background =
                "#6C63FF";
              (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
              (e.currentTarget as HTMLAnchorElement).style.border = "none";
            }}
          >
            Become a Member
          </a>
        </div>
      </section>

      {/* Search Features Comparison */}
      <section
        style={{
          maxWidth: 1200,
          margin: "48px auto 0 auto",
          padding: 32,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
        }}
      >
        <h2
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 32,
            textAlign: "center",
            marginBottom: 32,
          }}
        >
          Our Search Technology
        </h2>
        <div
          style={{
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {searchFeatures.map((feature, idx) => (
            <div
              key={idx}
              style={{
                background: "#f8f8ff",
                borderRadius: 14,
                boxShadow: "0 2px 8px #e0e7ff",
                padding: 24,
                minWidth: 300,
                maxWidth: 350,
                flex: "1 1 300px",
              }}
            >
              <img
                src={feature.image}
                alt={feature.type}
                style={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              />
              <h3
                style={{
                  color: "#222",
                  fontWeight: 700,
                  fontSize: 24,
                  marginBottom: 16,
                  textAlign: "center",
                }}
              >
                {feature.type}
              </h3>
              <ul style={{ listStyle: "none", padding: 0, marginBottom: 16 }}>
                {feature.features.map((feat, featIdx) => (
                  <li
                    key={featIdx}
                    style={{
                      color: "#333",
                      fontSize: 14,
                      marginBottom: 6,
                      paddingLeft: 20,
                      position: "relative",
                      fontWeight: 400,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "2px",
                      }}
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="#6C63FF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {feat}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  padding: 14,
                  fontSize: 13,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#333", fontWeight: 500 }}>
                    Capacity Filtering:
                  </span>{" "}
                  {feature.capacity === "✅" ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="#10b981"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#10b981"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="#ef4444"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#333", fontWeight: 500 }}>
                    Natural Language:
                  </span>{" "}
                  {feature.naturalLanguage === "✅" ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="#10b981"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#10b981"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="#ef4444"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#333", fontWeight: 500 }}>
                    All Basic Features:
                  </span>{" "}
                  {feature.allBasicFeatures === "✅" ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="#10b981"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#10b981"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="#ef4444"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#333", fontWeight: 500 }}>
                    Separate Codebase:
                  </span>{" "}
                  {feature.separateCodebase === "✅" ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="#10b981"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#10b981"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="#ef4444"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        style={{
          maxWidth: 1100,
          margin: "48px auto 0 auto",
          padding: 32,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
        }}
      >
        <h2
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 32,
            textAlign: "center",
            marginBottom: 32,
          }}
        >
          Why Choose EventNova?
        </h2>
        <div
          style={{
            display: "flex",
            gap: 32,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {whyChooseUs.map((item, idx) => (
            <div
              key={idx}
              style={{
                background: "#f8f8ff",
                borderRadius: 14,
                boxShadow: "0 2px 8px #e0e7ff",
                padding: 28,
                minWidth: 220,
                maxWidth: 260,
                textAlign: "center",
                transition: "transform 0.2s",
                cursor: "pointer",
                flex: "1 1 220px",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.transform = "scale(1.04)")
              }
              onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px auto",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                {item.icon === "💎" && (
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 3H18L22 9L12 22L2 9L6 3Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11 3L8 9L12 22L16 9L13 3H11Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {item.icon === "⚡" && (
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {item.icon === "🤝" && (
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {item.icon === "🔒" && (
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <h3
                style={{
                  color: "#222",
                  fontWeight: 700,
                  fontSize: 20,
                  marginBottom: 12,
                }}
              >
                {item.title}
              </h3>
              <p
                style={{
                  color: "#333",
                  fontSize: 15,
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline Section */}
      <section
        style={{
          maxWidth: 1100,
          margin: "48px auto 0 auto",
          padding: 32,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
        }}
      >
        <h2
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 32,
            textAlign: "center",
            marginBottom: 32,
          }}
        >
          Our Journey
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {timeline.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                gap: 24,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: "1 1 300px", minWidth: 300 }}>
                <img
                  src={item.image}
                  alt={item.title}
                  style={{
                    width: "100%",
                    height: 200,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              </div>
              <div style={{ flex: "1 1 300px" }}>
                <div
                  style={{
                    background: "#6C63FF",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: 20,
                    display: "inline-block",
                    marginBottom: 12,
                    fontWeight: "bold",
                  }}
                >
                  {item.year}
                </div>
                <h3
                  style={{
                    color: "#222",
                    fontWeight: 700,
                    fontSize: 24,
                    marginBottom: 8,
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    color: "#333",
                    fontSize: 16,
                    lineHeight: 1.6,
                    fontWeight: 400,
                  }}
                >
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <div
        className="testimonials-area pb-60"
        style={{ background: "#f8f8ff", marginTop: 48, padding: "60px 0" }}
      >
        <div className="container">
          <div className="section-title text-center mb-40">
            <h2
              style={{
                color: "#222",
                fontWeight: 800,
                fontSize: 32,
                marginBottom: 8,
              }}
            >
              What Our Users Say
            </h2>
            <p style={{ color: "#666", fontSize: 16, fontWeight: 400 }}>
              Real feedback from planners, coordinators, and creative teams
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 32,
              justifyContent: "center",
            }}
          >
            {loadingTestimonials ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                Loading testimonials...
              </div>
            ) : testimonialsError ? (
              <div style={{ textAlign: "center", color: "red", padding: 40 }}>
                {testimonialsError}
              </div>
            ) : testimonials.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, fontSize: 18 }}>
                No testimonials found.
              </div>
            ) : (
              testimonials.map((testimonial, idx) => {
                const avatar =
                  testimonial.avatar ||
                  testimonial.image ||
                  testimonial.photoUrl ||
                  "assets/img/default-avatar.png";
                const name =
                  testimonial.name ||
                  testimonial.fullName ||
                  testimonial.userName ||
                  "Happy Client";
                const role =
                  testimonial.role ||
                  testimonial.title ||
                  testimonial.userRole ||
                  "EventNova User";
                const feedback =
                  testimonial.feedback ||
                  testimonial.text ||
                  testimonial.comment ||
                  "";
                const rating = testimonial.rating || testimonial.stars || 5;

                return (
                  <div
                    key={testimonial.id || idx}
                    style={{
                      background: "#fff",
                      borderRadius: 16,
                      boxShadow: "0 2px 8px #eee",
                      padding: 32,
                      maxWidth: 340,
                      minWidth: 260,
                      flex: "1 1 260px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <img
                      src={avatar}
                      alt={name}
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        objectFit: "cover",
                        marginBottom: 12,
                      }}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#222",
                        fontSize: 18,
                        marginBottom: 4,
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        color: "#6C63FF",
                        fontSize: 14,
                        marginBottom: 12,
                      }}
                    >
                      {role}
                    </div>
                    <p
                      style={{
                        color: "#333",
                        fontSize: 15,
                        marginBottom: 12,
                        lineHeight: 1.6,
                        fontWeight: 400,
                        fontStyle: "italic",
                      }}
                    >
                      "{feedback}"
                    </p>
                    <div style={{ color: "#FFD700", fontSize: 18 }}>
                      {"★".repeat(Math.max(1, Math.min(5, rating)))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <section
        style={{
          maxWidth: 900,
          margin: "64px auto 0 auto",
          padding: 40,
          background: "linear-gradient(90deg, #6C63FF 60%, #b7b8f7 100%)",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            color: "#ffffff",
            fontWeight: 900,
            fontSize: 32,
            marginBottom: 16,
            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          Ready to Plan Your Next Event?
        </h2>
        <p
          style={{
            color: "#ffffff",
            fontSize: 20,
            marginBottom: 24,
            fontWeight: 400,
            textShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        >
          Join thousands of happy clients and vendors who trust EventNova for
          their most important moments.
        </p>
        <a
          href="/vendors"
          style={{
            background: "#fff",
            color: "#6C63FF",
            fontWeight: 700,
            fontSize: 18,
            padding: "14px 36px",
            borderRadius: 8,
            textDecoration: "none",
            boxShadow: "0 2px 8px #eee",
            transition: "background 0.2s, color 0.2s",
          }}
          onMouseOver={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.background = "#6C63FF";
            (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
          }}
          onMouseOut={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.background = "#fff";
            (e.currentTarget as HTMLAnchorElement).style.color = "#6C63FF";
          }}
        >
          Browse Vendors
        </a>
      </section>
    </div>
  );
};

export default About;
